import { TrackedAnchor } from '@objectiv/tracker-react';
import React, { forwardRef } from 'react';

import { funnelLinkRegex, internalLinkRegex, myInsifyLinkRegex } from '~globals';
import { useFunnelUrl } from '~hooks/useFunnelUrl';
import { useMyInsifyUrl } from '~hooks/useMyInsifyUrl';
import { GTMEvent, useTagManager } from '~hooks/useTagManager';
import { isUrlFile } from '~utils/string';
import { TrackedLink } from '../TrackedLink';

export type BaseLinkProps = React.ComponentPropsWithoutRef<'a'> & {
  href: string;
  trackingId?: string;
  activeClassName?: string;
};

export const BaseLink = forwardRef<HTMLAnchorElement, BaseLinkProps>((props, ref) => {
  if (internalLinkRegex.test(props.href) || props.href.startsWith('/')) return <InternalLink ref={ref} {...props} />;
  if (funnelLinkRegex.test(props.href)) return <FunnelLink ref={ref} {...props} />;
  if (myInsifyLinkRegex.test(props.href)) return <MyInsifyLink ref={ref} {...props} />;
  return <ExternalLink ref={ref} {...props} />;
});

const InternalLink = forwardRef<HTMLAnchorElement, BaseLinkProps>(
  ({ href, children, target: _target, ...props }, ref) => {
    const isFile = isUrlFile(href);
    const hrefWithTrailingSlash =
      href.endsWith('/') || isFile || href.split('/').pop()?.includes('.') ? href : `${href}/`;

    return (
      <TrackedLink
        ref={ref}
        {...props}
        to={hrefWithTrailingSlash.replace(internalLinkRegex, '/')}
        rel={isFile ? 'noreferrer' : undefined}
      >
        {children}
      </TrackedLink>
    );
  },
);

const ExternalLink = forwardRef<HTMLAnchorElement, BaseLinkProps>((props, ref) => {
  return (
    <TrackedAnchor
      ref={ref}
      {...props}
      objectiv={{ id: props.trackingId || props.id }}
      rel={isUrlFile(props.href) ? 'noreferrer' : undefined}
    />
  );
});

const FunnelLink = forwardRef<HTMLAnchorElement, BaseLinkProps>(({ href, ...props }, ref) => {
  const { formatFunnelLink } = useFunnelUrl();

  return <ExternalLink ref={ref} {...props} href={formatFunnelLink(href)} target='_self' />;
});

const MyInsifyLink = forwardRef<HTMLAnchorElement, BaseLinkProps>(({ href, onClick, ...props }, ref) => {
  const { formatMyInsifyLink } = useMyInsifyUrl();
  const { sendEvent } = useTagManager();

  return (
    <ExternalLink
      ref={ref}
      {...props}
      href={formatMyInsifyLink(href)}
      target='_blank'
      onClick={(event) => {
        sendEvent(GTMEvent.MY_INSIFY_LOGIN);
        onClick?.(event);
      }}
    />
  );
});
